/**
 * Audit Service
 * Unified service for logging business events to activity_events table
 * 
 * IMPORTANT: This service handles BUSINESS events (user actions, admin changes, etc.)
 * For TECHNICAL logs (errors, warnings), use logger.ts instead.
 * 
 * Features for scale (350K users, 5K DAU):
 * - Batching: accumulates events, flushes every 10 events or 5 seconds
 * - sendBeacon: ensures events are sent on page unload
 * - Rate limiting: max 50 events per minute per client
 * - Debouncing: prevents duplicate frequent events
 */

import { supabase } from '@/integrations/supabase/client';
import { logger } from './logger';
import { supabaseConfig } from '@/lib/config/environment';
import { getCachedUserId } from '@/contexts/AuthContext';
import type { Json } from '@/integrations/supabase/types';

/**
 * Standard event types for activity_events
 * Must match codifier_items where codifier_key = 'activity_event_type'
 */
export type AuditEventType =
  // User auth events
  | 'user_registered'
  | 'user_logged_in'
  | 'user_logged_out'
  | 'password_reset_requested'
  | 'password_changed'
  // Profile events
  | 'profile_updated'
  | 'avatar_changed'
  | 'account_deleted'
  // Application events
  | 'application_submitted'
  | 'application_approved'
  | 'application_rejected'
  // Listing events
  | 'listing_created'
  | 'listing_archived'
  | 'listing_unarchived'
  | 'listing_moderated'
  // Moderation events
  | 'moderation_approved'
  | 'moderation_rejected'
  | 'curator_blocked'
  | 'curator_unblocked'
  // Pet events
  | 'pet_adopted'
  // Admin events
  | 'admin_setting_changed'
  | 'user_role_changed'
  | 'feature_flag_changed'
  // Report events
  | 'user_report_submitted'
  // Consent events
  | 'ads_consent_granted'
  | 'ads_consent_revoked'
  | 'personal_data_consent_granted'
  // Generic
  | 'system'
  | 'user'
  | 'unknown';

/**
 * Entity types for activity_events
 * Must match entity_types table
 */
export type AuditEntityType =
  | 'user'
  | 'listing'
  | 'application'
  | 'pet'
  | 'organization'
  | 'chat_room'
  | 'chat_message'
  | 'help_request'
  | 'comment'
  | 'notification'
  | 'form_response'
  | 'moderation_check'
  | 'app_setting';

export interface AuditEvent {
  eventType: AuditEventType | string;
  entityType?: AuditEntityType | string;
  entityId?: string;
  actorUserId?: string;
  payload?: Record<string, unknown>;
}

interface AuditResult {
  success: boolean;
  error?: string;
}

interface BufferedEvent {
  event_type: string;
  event_type_codifier_key: string;
  entity_type: string | null;
  entity_id: string | null;
  actor_user_id: string | null;
  payload_json: Json | null;
}

// Buffer configuration
const BUFFER_SIZE = 10;
const FLUSH_INTERVAL_MS = 5000;
const RATE_LIMIT_PER_MINUTE = 50;

// Buffer state
let eventBuffer: BufferedEvent[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let eventCountInMinute = 0;
let minuteResetTimer: ReturnType<typeof setTimeout> | null = null;
let cachedUserId: string | null = null;

/**
 * Get cached user ID from AuthContext
 */
function getCurrentUserId(): string | null {
  if (cachedUserId) return cachedUserId;
  
  cachedUserId = getCachedUserId();
  return cachedUserId;
}

// Reset cached user on auth state change
if (typeof window !== 'undefined') {
  supabase.auth.onAuthStateChange(() => {
    cachedUserId = null;
  });
}

/**
 * Flush buffered events to database
 */
async function flushBuffer(): Promise<void> {
  if (eventBuffer.length === 0) return;
  
  const eventsToFlush = [...eventBuffer];
  eventBuffer = [];
  
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  
  try {
    const { error } = await supabase.from('activity_events').insert(eventsToFlush);
    
    if (error) {
      logger.warn('Failed to flush audit events', { 
        error, 
        context: { feature: 'audit' },
        data: { count: eventsToFlush.length },
      });
    }
  } catch (e) {
    logger.warn('Audit flush exception', { 
      error: e, 
      context: { feature: 'audit' },
      data: { count: eventsToFlush.length },
    });
  }
}

/**
 * Schedule buffer flush
 */
function scheduleFlush(): void {
  if (flushTimer) return;
  
  flushTimer = setTimeout(() => {
    flushTimer = null;
    flushBuffer();
  }, FLUSH_INTERVAL_MS);
}

/**
 * Reset rate limit counter every minute
 */
function ensureRateLimitReset(): void {
  if (minuteResetTimer) return;
  
  minuteResetTimer = setTimeout(() => {
    eventCountInMinute = 0;
    minuteResetTimer = null;
  }, 60000);
}

// Flush on page unload using sendBeacon
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    if (eventBuffer.length > 0) {
      // Use sendBeacon for reliable delivery on page close
      const payload = JSON.stringify({ events: eventBuffer });
      // Fallback to sync flush if sendBeacon not available
      navigator.sendBeacon?.(
        `${supabaseConfig.url}/rest/v1/activity_events`,
        payload
      );
    }
  });
}

/**
 * Log an audit event to activity_events table
 * Uses buffering for performance at scale
 */
export async function logAuditEvent(event: AuditEvent): Promise<AuditResult> {
  // Rate limiting
  ensureRateLimitReset();
  if (eventCountInMinute >= RATE_LIMIT_PER_MINUTE) {
    return { success: false, error: 'Rate limit exceeded' };
  }
  eventCountInMinute++;
  
  try {
    // Get current user if actorUserId not provided (synchronous now)
    const actorId = event.actorUserId ?? getCurrentUserId();

    // Sanitize payload - remove any PII-like fields
    const sanitizedPayload = event.payload 
      ? (sanitizeAuditPayload(event.payload) as Json) 
      : null;

    const bufferedEvent: BufferedEvent = {
      event_type: event.eventType,
      event_type_codifier_key: 'activity_event_type',
      entity_type: event.entityType || null,
      entity_id: event.entityId || null,
      actor_user_id: actorId || null,
      payload_json: sanitizedPayload,
    };
    
    eventBuffer.push(bufferedEvent);
    
    // Flush immediately if buffer is full
    if (eventBuffer.length >= BUFFER_SIZE) {
      await flushBuffer();
    } else {
      scheduleFlush();
    }

    return { success: true };
  } catch (e) {
    logger.warn('Audit event exception', { 
      error: e, 
      context: { feature: 'audit' },
      data: { eventType: event.eventType },
    });
    return { success: false, error: e instanceof Error ? e.message : 'Unknown error' };
  }
}

/**
 * Force flush all pending events (useful before navigation)
 */
export async function flushAuditEvents(): Promise<void> {
  await flushBuffer();
}

/**
 * Remove potentially sensitive fields from audit payloads
 */
function sanitizeAuditPayload(payload: Record<string, unknown>): Record<string, unknown> {
  const sensitiveKeys = [
    'password', 'token', 'secret', 'key', 'auth', 'credential',
    'email', 'phone', 'address', 'ip', 'cookie', 'session',
  ];
  
  const sanitized: Record<string, unknown> = {};
  
  for (const [key, value] of Object.entries(payload)) {
    const lowerKey = key.toLowerCase();
    const isSensitive = sensitiveKeys.some(sk => lowerKey.includes(sk));
    
    if (isSensitive) {
      sanitized[key] = '[REDACTED]';
    } else if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
      sanitized[key] = sanitizeAuditPayload(value as Record<string, unknown>);
    } else {
      sanitized[key] = value;
    }
  }
  
  return sanitized;
}

/**
 * Convenience methods for common audit events
 */
export const audit = {
  // Auth events
  userRegistered: (userId: string, method: 'password' | 'google' | 'vk' = 'password') =>
    logAuditEvent({
      eventType: 'user_registered',
      entityType: 'user',
      entityId: userId,
      actorUserId: userId,
      payload: { method, timestamp: new Date().toISOString() },
    }),

  userLoggedIn: (userId: string, method: 'password' | 'google' | 'vk' = 'password') =>
    logAuditEvent({
      eventType: 'user_logged_in',
      entityType: 'user',
      entityId: userId,
      actorUserId: userId,
      payload: { method, timestamp: new Date().toISOString() },
    }),

  userLoggedOut: (userId: string) =>
    logAuditEvent({
      eventType: 'user_logged_out',
      entityType: 'user',
      entityId: userId,
      actorUserId: userId,
      payload: { timestamp: new Date().toISOString() },
    }),

  // Profile events
  profileUpdated: (userId: string, changedFields: string[]) =>
    logAuditEvent({
      eventType: 'profile_updated',
      entityType: 'user',
      entityId: userId,
      actorUserId: userId,
      payload: { changedFields, timestamp: new Date().toISOString() },
    }),

  // Admin events
  adminSettingChanged: (settingKey: string, oldValue: unknown, newValue: unknown) =>
    logAuditEvent({
      eventType: 'admin_setting_changed',
      entityType: 'app_setting',
      entityId: settingKey,
      payload: { 
        setting_key: settingKey, 
        old_value: String(oldValue), 
        new_value: String(newValue),
        timestamp: new Date().toISOString(),
      },
    }),

  userRoleChanged: (targetUserId: string, oldRole: string | null, newRole: string) =>
    logAuditEvent({
      eventType: 'user_role_changed',
      entityType: 'user',
      entityId: targetUserId,
      payload: { 
        old_role: oldRole, 
        new_role: newRole,
        timestamp: new Date().toISOString(),
      },
    }),

  featureFlagChanged: (flagKey: string, enabled: boolean) =>
    logAuditEvent({
      eventType: 'feature_flag_changed',
      entityType: 'app_setting',
      entityId: flagKey,
      payload: { 
        flag_key: flagKey, 
        enabled,
        timestamp: new Date().toISOString(),
      },
    }),

  // Moderation events
  listingModerated: (listingId: string, action: 'approved' | 'rejected', comment?: string) =>
    logAuditEvent({
      eventType: action === 'approved' ? 'moderation_approved' : 'moderation_rejected',
      entityType: 'listing',
      entityId: listingId,
      payload: { 
        action, 
        comment: comment ? '[HAS_COMMENT]' : null, // Don't store actual comment text
        timestamp: new Date().toISOString(),
      },
    }),

  curatorBlocked: (curatorId: string, reason?: string) =>
    logAuditEvent({
      eventType: 'curator_blocked',
      entityType: 'user',
      entityId: curatorId,
      payload: { 
        has_reason: !!reason,
        timestamp: new Date().toISOString(),
      },
    }),

  curatorUnblocked: (curatorId: string) =>
    logAuditEvent({
      eventType: 'curator_unblocked',
      entityType: 'user',
      entityId: curatorId,
      payload: { 
        timestamp: new Date().toISOString(),
      },
    }),

  // Application events
  applicationSubmitted: (applicationId: string, listingId: string) =>
    logAuditEvent({
      eventType: 'application_submitted',
      entityType: 'application',
      entityId: applicationId,
      payload: { 
        listing_id: listingId,
        timestamp: new Date().toISOString(),
      },
    }),
};

export default audit;
